import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../../firebase/firebase.config";
import { onAuthStateChanged, signInWithEmailAndPassword } from "firebase/auth";
import "./Auth.css";

import { useAuth } from "../../contexts/AuthContext";

const VerifyEmail = () => {
  const { login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { email, password } = location.state || {};
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [authReady, setAuthReady] = useState(false);

  // Redirect to signup if email, name, or password is missing
  React.useEffect(() => {
    if (!email || !password) {
      setError("Session expired or invalid access. Please sign up again.");
      setTimeout(() => navigate("/signup"), 2500);
    }
  }, [email, password, navigate]);

  // Wait for Firebase auth to be ready
  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const handleCheckVerification = async () => {
    setChecking(true);
    setError("");
    try {
      // Just check if user is authenticated and reload the user
      if (!auth.currentUser) {
        if (!password) {
          setError(
            "Session expired or missing password. Please sign up again."
          );
          setTimeout(() => navigate("/signup"), 2500);
          return;
        }
        try {
          await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
          setError(
            "Session expired or invalid credentials. Please sign up again."
          );
          setTimeout(() => navigate("/signup"), 2500);
          return;
        }
      }
      await auth.currentUser.reload();

      if (auth.currentUser.emailVerified) {
        // Ensure sessionStorage is updated so AuthContext sees the user as authenticated
        const userData = {
          email: auth.currentUser.email,
          id: auth.currentUser.uid,
        };
        login(userData); // Update context authentication state
        navigate("/app");
      } else {
        setError(
          "Email not verified yet. Please check your inbox or spam folder."
        );
      }
    } catch (err) {
      console.error("Verification error:", err);
      setError(err.message || "Failed to check verification status.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="music-note note-1">🎵</div>
        <div className="music-note note-2">🎶</div>
        <div className="music-note note-3">🎼</div>
      </div>
      <div
        className="auth-card"
        style={{
          maxWidth: "420px",
          margin: "40px auto",
          padding: "32px",
          boxShadow: "0 6px 32px rgba(0,0,0,0.25)",
          borderRadius: "18px",
          background: "rgba(30,30,40,0.97)",
        }}
      >
        <h2 style={{ textAlign: "center", fontSize: 28, marginBottom: 18 }}>
          <span role="img" aria-label="mail">
            📧
          </span>{" "}
          Verify Your Email
        </h2>
        <p
          style={{
            textAlign: "center",
            fontSize: 17,
            marginBottom: 18,
            lineHeight: 1.6,
          }}
        >
          <span role="img" aria-label="sparkles">
            ✨
          </span>{" "}
          We've sent a verification email to <b>{email}</b>.<br />
          <span role="img" aria-label="inbox">
            📥
          </span>{" "}
          Please check your inbox or spam folder and click the verification
          link.
          <br />
          <span role="img" aria-label="support">
            🛟
          </span>{" "}
          If you do not receive the email, contact support at{" "}
          <a
            href="mailto:abc@gmail.com"
            style={{ color: "#4facfe", textDecoration: "underline" }}
          >
            abc@gmail.com
          </a>
          .
        </p>
        <button
          onClick={handleCheckVerification}
          disabled={checking || !authReady}
          className="auth-button"
          style={{
            marginTop: "18px",
            width: "100%",
            fontSize: 18,
            padding: "12px 0",
            borderRadius: 8,
          }}
        >
          {!authReady
            ? "Loading..."
            : checking
            ? "Checking..."
            : "🎉 I've Verified My Email"}
        </button>
        {error && (
          <div
            className="auth-error"
            style={{
              marginTop: "18px",
              background: "#3b1818",
              color: "#fff",
              borderRadius: 8,
              padding: "12px",
              textAlign: "center",
              fontWeight: 500,
              fontSize: 16,
              border: "1.5px solid #c0392b",
            }}
          >
            <span role="img" aria-label="error">
              ❗
            </span>{" "}
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
